import React, { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { ru } from "date-fns/locale";

/* Real DD.MM.YYYY date picker.
 * Rules:
 *  - manual typing accepts D/M/Y, D.M.YYYY, DDMMYYYY, DD.MM.YYYY
 *  - year MUST be exactly 4 digits
 *  - month 1..12, day 1..(days in month)
 *  - impossible dates rejected (returns null on blur if invalid)
 *  - calendar opens on `defaultMonth` (props) or `value` or today — never 1700
 *  - `minDate` blocks earlier days (used for "не в прошлом")
 *  - value is an ISO "YYYY-MM-DD" string OR null; onChange returns same
 */

function pad(n) { return String(n).padStart(2, "0"); }
function toDDMMYYYY(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}
function fromDateObj(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }

function parseDDMMYYYY(raw) {
  if (!raw) return null;
  // Accept separators . / -  and pure digits
  const cleaned = raw.trim();
  const m = cleaned.match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{4})$/) || cleaned.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (!m) return null;
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (String(yyyy).length !== 4) return null;
  if (mm < 1 || mm > 12) return null;
  const daysIn = new Date(yyyy, mm, 0).getDate();
  if (dd < 1 || dd > daysIn) return null;
  return `${yyyy}-${pad(mm)}-${pad(dd)}`;
}

export default function DateField({
  value, onChange, minDate, placeholder = "ДД.ММ.ГГГГ", testid = "mo-date",
  clearable = true,
}) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState(toDDMMYYYY(value));
  React.useEffect(() => { setRaw(toDDMMYYYY(value)); }, [value]);

  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    const [y, m, d] = value.split("-").map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d);
  }, [value]);

  const defaultMonth = selectedDate || new Date();
  const commit = (isoOrNull) => {
    onChange(isoOrNull);
    setRaw(toDDMMYYYY(isoOrNull));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={raw}
          onChange={(e) => setRaw(e.target.value.replace(/[^\d./-]/g, ""))}
          onBlur={() => {
            if (!raw) return commit(null);
            const iso = parseDDMMYYYY(raw);
            if (iso) commit(iso); else setRaw(toDDMMYYYY(value)); // revert if garbage
          }}
          placeholder={placeholder}
          className="w-full h-10 pl-3 pr-16 rounded-lg text-[13px] outline-none"
          style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
          data-testid={testid}
        />
        <div className="absolute inset-y-0 right-1.5 flex items-center gap-0.5">
          {clearable && value && (
            <button
              type="button"
              onClick={() => commit(null)}
              className="w-7 h-7 rounded-full grid place-items-center hover:bg-black/5"
              style={{ color: "#8B6F4B" }}
              aria-label="Очистить"
              data-testid={`${testid}-clear`}
            >
              <X size={13} strokeWidth={1.8} />
            </button>
          )}
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-7 h-7 rounded-full grid place-items-center hover:bg-black/5"
              style={{ color: "#8B6F4B" }}
              aria-label="Открыть календарь"
              data-testid={`${testid}-open`}
            >
              <CalendarIcon size={14} strokeWidth={1.8} />
            </button>
          </PopoverTrigger>
        </div>
      </div>
      <PopoverContent align="start" className="p-0 w-auto">
        <Calendar
          mode="single"
          selected={selectedDate}
          defaultMonth={defaultMonth}
          onSelect={(d) => {
            if (!d) return;
            commit(fromDateObj(d));
            setOpen(false);
          }}
          disabled={minDate ? { before: minDate } : undefined}
          locale={ru}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
